/**
 * Files the export needs that the browser lost (W3-07; UX-065 / UX-066 / UX-067). `persist.hasAsset` is the presence
 * probe (a stored record whose bytes are gone counts as absent). The Publish workspace and the Export dialog block
 * the export with a plain "missing from this browser's storage" problem, and the illustration says "missing"
 * instead of "No illustration". Probes run when the list of files changes, when the window regains focus and, while
 * something is missing, every few seconds (choosing the same file again in Assets restores it).
 */

import { useEffect, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { AssetId, ChartDoc, DocIndexes } from '../../core/model/types';

/** Re-probe period while a file is missing. */
export const MISSING_RECHECK_MS = 3000;

/** Marker of a persist rejection whose stored bytes are gone (persist/assetStore.ts `ASSET_BYTES_MISSING`). */
export const ASSET_BYTES_MISSING = 'asset bytes missing';

/** True when an error (a persist rejection, an export failure text) says that stored file bytes are missing. */
export function isBytesMissing(err: unknown): boolean {
  const msg =
    typeof err === 'string'
      ? err
      : typeof err === 'object' && err !== null && typeof (err as { message?: unknown }).message === 'string'
        ? (err as { message: string }).message
        : '';
  return msg.includes(ASSET_BYTES_MISSING);
}

/** The file name an export failure names (`the file "x.png" could not be read`), or null. */
export function failedFileName(message: string): string | null {
  return /the file "([^"]+)" could not be read/.exec(message)?.[1] ?? null;
}

/** Stored files the export of `doc` writes: the song, the illustration and every used file (not placeholders). */
export function exportedAssetIds(doc: ChartDoc, idx: DocIndexes): AssetId[] {
  const out: AssetId[] = [];
  for (const a of doc.assets.values()) {
    if (a.missing === true) continue;
    const u = idx.assetUsers(a.id);
    const used = u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length > 0;
    if (used || doc.meta.music === a.id || doc.meta.illustration === a.id) out.push(a.id);
  }
  return out.sort();
}

/** The ids among `ids` whose bytes are missing (empty until the first probe answers). */
export function useStoredMissing(
  services: Pick<AppServices, 'persist'>,
  ids: readonly AssetId[],
): ReadonlySet<AssetId> {
  const key = ids.join(',');
  const [state, setState] = useState<{ readonly key: string; readonly missing: ReadonlySet<AssetId> }>({
    key: '',
    missing: new Set(),
  });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const list = key === '' ? [] : (key.split(',') as AssetId[]);
    let alive = true;
    void Promise.all(
      list.map((id) =>
        services.persist.hasAsset(id).then(
          (present) => present,
          () => true,
        ),
      ),
    ).then((present) => {
      if (!alive) return;
      const missing = new Set(list.filter((_, i) => present[i] === false));
      setState((prev) =>
        prev.key === key && prev.missing.size === missing.size && [...missing].every((id) => prev.missing.has(id))
          ? prev
          : { key, missing },
      );
    });
    return () => {
      alive = false;
    };
  }, [services, key, tick]);
  const anyMissing = state.key === key && state.missing.size > 0;
  useEffect(() => {
    const again = (): void => {
      setTick((t) => t + 1);
    };
    window.addEventListener('focus', again);
    const timer = anyMissing ? setInterval(again, MISSING_RECHECK_MS) : null;
    return () => {
      window.removeEventListener('focus', again);
      if (timer !== null) clearInterval(timer);
    };
  }, [anyMissing]);
  return state.key === key ? state.missing : EMPTY;
}

const EMPTY: ReadonlySet<AssetId> = new Set();
